import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { getApi, putApi } from "../../utils/api";
import Cookies from "js-cookie";
import {
  GET_LEAVE_REQUEST_HISTORY,
  LEAVE_BALANCE,
  LEAVE_LIST,
  API_SQE_EMPLOYEE_REQUESTED_LEAVES,
} from "../../config/Endpoints";
import ApplyLeave from "./ApplyLeave";
import { TailSpin, ThreeDots } from "react-loader-spinner";
import { formatForDisplay } from "../../utils/helpers";
import { getUser } from "../../utils/Storage";
import { showSuccess, showError } from "../../v2/utils/toast.ts";
import { getApiErrorMessage } from "../../v2/utils/api-error-message.ts";
import * as Yup from "yup";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import axios from "axios";
import LeaveRequestHistoryModal from "../../components/LeaveRequestHistoryModal";
import { useUser } from "../../v2/lib/rbac/useUser.ts";
import { formatLeaveTypeLabel } from "./utils/leave-type-label";
import PageShell from "../../v2/components/shared/page-shell/page-shell.tsx";
import { Card } from "../../v2/components/shared/card/card.tsx";
import { EmptyStateMessage } from "../../v2/components/shared/empty-state-message/empty-state-message.tsx";
import {
  Badge,
  Button,
  Modal,
  ResponsiveDataTable,
  TextArea,
} from "../../v2/components/ui/index.ts";
import {
  AlertTriangle,
  CalendarDays,
  Eye,
  Home,
  Plus,
  Wallet,
} from "lucide-react";
import styles from "./leave.module.css";
import { LeaveSegment, resolveLeaveSegment } from "./config/leave-segments";
import { getLeaveSegmentConfig } from "./config/leave-segment-config";

const STATUS_MAP = {
  PENDING: { label: "Pending", variant: "warning" },
  APPROVED: { label: "Approved", variant: "success" },
  REJECTED: { label: "Rejected", variant: "danger" },
  WITHDRAW: { label: "Withdrawn", variant: "neutral" },
  WITHDRAWN: { label: "Withdrawn", variant: "neutral" },
};

function formatLeaveBalanceCard(key, value) {
  if (key === "workFromHomeLeave") {
    return value ? value : 0;
  }
  if (key === "negativeBalance") {
    return value ? `-${value}` : "0";
  }
  return value ?? 0;
}

function LeaveBalanceCardIcon({ name }) {
  const iconProps = { size: 16, strokeWidth: 2, "aria-hidden": true };
  if (name === "home") return <Home {...iconProps} />;
  if (name === "wallet") return <Wallet {...iconProps} />;
  if (name === "alert") return <AlertTriangle {...iconProps} />;
  return <CalendarDays {...iconProps} />;
}

const LeaveList = ({ title, description }) => {
  const [leaveList, setLeaveList] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [filteredItems, setFilteredItems] = useState(null);
  const [selectedleaveRequest, setSelectedleaveRequest] = useState({});
  const [reason, setReason] = useState("");
  const accessToken = Cookies.get("accessToken");
  const userData = JSON.parse(getUser("userData"));
  const user = useUser();
  const segment = resolveLeaveSegment({
    location: user?.location ?? userData?.location,
    empType: user?.empType ?? userData?.empType,
  });
  const leaveSegmentConfig = getLeaveSegmentConfig(segment);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeaves, setTotalLeaves] = useState(0);
  const [limit, setLimit] = useState(10);
  const [showWFHModal, setShowWFHModal] = useState(false);
  const [errors, setErrors] = useState({});
  const validationSchema = Yup.object().shape({
    reason: Yup.string()
      .min(5, "Reason must be at least 5 characters long.")
      .required("Reason is required."),
  });
  const [ledgerData, setLedgerData] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const withdrawReasonId = React.useId().replace(/:/g, "");

  /** Defer unmount until after the browser finishes the click, so it cannot hit "Apply Leave" under the backdrop. */
  const closeWFHReminderModal = React.useCallback(() => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setShowWFHModal(false));
    });
  }, []);

  const fetchLeaveLedger = (requestId) => {
    if (!requestId) return;

    setLedgerLoading(true);
    getApi(`${GET_LEAVE_REQUEST_HISTORY}/${requestId}`, accessToken)
      .then((res) => {
        if (res.status === 200) {
          setLedgerData(res.data?.data || []);
        }
        setLedgerLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLedgerLoading(false);
      });
  };

  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    setTableData(
      filteredItems?.map((item) => {
        return {
          from: formatForDisplay(item.fromDate),
          to: formatForDisplay(item.toDate),
          type: formatLeaveTypeLabel(item.leaveType),
          reason: item.reason || "-",
          leaveCount: item.totalDays,
          status: item.status === "WITHDRAW" ? "WITHDRAWN" : item.status,
          _original: item,
        };
      }) || [],
    );
  }, [filteredItems]);

  const tableColumns = useMemo(
    () => [
      {
        id: "from",
        header: "From",
        accessor: "from",
        sortable: true,
        getSortValue: (row) =>
          row._original?.fromDate
            ? new Date(row._original.fromDate).getTime()
            : 0,
      },
      {
        id: "to",
        header: "To",
        accessor: "to",
        sortable: true,
        getSortValue: (row) =>
          row._original?.toDate
            ? new Date(row._original.toDate).getTime()
            : 0,
      },
      {
        id: "type",
        header: "Leave Type",
        accessor: "type",
        sortable: true,
      },
      {
        id: "leaveCount",
        header: "Leave Count",
        accessor: "leaveCount",
        sortable: true,
      },
      {
        id: "reason",
        header: "Reason",
        accessor: "reason",
        sortable: false,
        truncate: true,
        getTitleText: (row) =>
          String(row._original?.reason ?? row.reason ?? "-"),
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        nowrap: true,
        getSortValue: (row) => {
          const key = row._original?.status ?? row.status;
          return STATUS_MAP[key]?.label ?? String(key ?? "");
        },
        render: (row) => {
          const key = row._original?.status ?? row.status;
          const meta = STATUS_MAP[key] || {};
          return (
            <Badge variant={meta.variant ?? "neutral"}>{meta.label ?? key}</Badge>
          );
        },
      },
      {
        id: "viewHistory",
        header: "View Details",
        sortable: false,
        nowrap: true,
        render: (row) => {
          const orig = row._original;
          return orig?.legacyLeaveId ? (
            <button
              type="button"
              className="action-btn action-btn--view"
              data-bs-toggle="modal"
              data-bs-target="#viewLedgerModal"
              onClick={() => fetchLeaveLedger(orig.legacyLeaveId)}
            >
              <Eye size={16} strokeWidth={2} aria-hidden />
              View
            </button>
          ) : (
            <button type="button" className="action-btn action-btn--view" disabled>
              <Eye size={16} strokeWidth={2} aria-hidden />
              View
            </button>
          );
        },
      },
      {
        id: "action",
        header: "Action",
        sortable: false,
        nowrap: true,
        render: (row) => {
          const orig = row._original;
          return (
            <>
              {orig?.status === "PENDING" && (segment === LeaveSegment.SQE || orig?.legacyLeaveId) ? (
                <button
                  type="button"
                  variant="danger-ghost"
                  className="action-btn action-btn--withdraw"
                  onClick={() => {
                    setSelectedleaveRequest(orig);
                    setWithdrawOpen(true);
                  }}
                >
                  Withdraw
                </button>
              ) : (
                <span className="text-muted">-</span>
              )}
            </>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- column defs stable; STATUS_MAP is module-level
    [segment],
  );

  const withdrawSummaryColumns = useMemo(
    () => [
      { id: "from", header: "From", accessor: "from", sortable: false },
      { id: "to", header: "To", accessor: "to", sortable: false },
      {
        id: "leaveType",
        header: "Leave Type",
        accessor: "leaveType",
        sortable: false,
      },
      {
        id: "reason",
        header: "Reason",
        accessor: "reason",
        sortable: false,
        truncate: true,
        getTitleText: (row) => String(row.reason ?? "-"),
      },
      {
        id: "leaveCount",
        header: "Leave Count",
        accessor: "leaveCount",
        sortable: false,
        nowrap: true,
      },
      {
        id: "status",
        header: "Status",
        sortable: false,
        nowrap: true,
        render: (row) => {
          const key = row.statusKey;
          const meta = STATUS_MAP[key] || {};
          return (
            <Badge variant={meta.variant ?? "neutral"}>
              {meta.label ?? key ?? "-"}
            </Badge>
          );
        },
        getTitleText: (row) => {
          const key = row.statusKey;
          return STATUS_MAP[key]?.label ?? String(key ?? "");
        },
      },
    ],
    [],
  );

  const withdrawSummaryRows = useMemo(() => {
    if (
      !selectedleaveRequest ||
      Object.keys(selectedleaveRequest).length === 0
    ) {
      return [];
    }
    return [
      {
        from: formatForDisplay(selectedleaveRequest.fromDate),
        to: formatForDisplay(selectedleaveRequest.toDate),
        leaveType: formatLeaveTypeLabel(selectedleaveRequest.leaveType),
        reason: selectedleaveRequest.reason ?? "-",
        leaveCount:
          selectedleaveRequest.totalDays != null
            ? String(selectedleaveRequest.totalDays)
            : "",
        statusKey: selectedleaveRequest.status ?? "",
      },
    ];
  }, [selectedleaveRequest]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    fetchLeaveBalance();
  }, []);

  useEffect(() => {
    fetchList();
  }, [currentPage, limit]);

  const fetchList = () => {
    setIsLoading(true);
    const requestedLeavesUrl = segment === LeaveSegment.SQE
      ? API_SQE_EMPLOYEE_REQUESTED_LEAVES(userData?.id)
      : `${LEAVE_LIST}/${userData?.id}/requested`;
    getApi(
      `${requestedLeavesUrl}?page=${currentPage - 1}&size=${limit}`,
      accessToken,
    )
      .then((res) => {
        if (res.status === 200) {
          const page = res.data?.data;
          const content = Array.isArray(page?.content) ? page.content : [];
          setLeaveList(content);
          setFilteredItems(content);
          setTotalPages(page?.totalPages ?? 1);
          setTotalLeaves(page?.totalElements ?? 0);
        }
        setIsLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setIsLoading(false);
      });
  };

  const fetchLeaveBalance = () => {
    setIsLoading(true);
    getApi(LEAVE_BALANCE + `/${userData?.id}`, accessToken)
      .then((res) => {
        if (res.status === 200) {
          setLeaveBalance(res.data?.data);
        }
        setIsLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setIsLoading(false);
      });
  };

  const handleReset = () => {
    setSelectedleaveRequest({});
    setReason("");
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      await validationSchema.validate(
        {
          reason: reason,
        },
        {
          abortEarly: false,
        },
      );

      const leaveId = segment === LeaveSegment.SQE
        ? selectedleaveRequest?.newRequestId ?? selectedleaveRequest?.legacyLeaveId
        : selectedleaveRequest?.legacyLeaveId ?? selectedleaveRequest?.laveId;
      if (leaveId == null) {
        setIsLoading(false);
        showError("This leave request cannot be withdrawn.");
        return;
      }

      const withdrawPromise =
        segment === LeaveSegment.US
          ? putApi(
            `${leaveSegmentConfig.withdrawEndpoint}?leaveId=${leaveId}&reason=${encodeURIComponent(
              reason,
            )}`,
            {},
            accessToken,
          )
          : axios.post(
            leaveSegmentConfig.withdrawEndpoint,
            {
              requestId: leaveId,
              employeeId: userData.id,
              reason,
            },
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            },
          );

      await withdrawPromise
        .then((res) => {
          setIsLoading(false);
          showSuccess(res?.data?.message ?? "Request updated.");

          fetchList();
          fetchLeaveBalance();
          document.getElementsByClassName("cancel-modal-withdraw")[0].click();
        })
        .catch((e) => {
          setIsLoading(false);
          showError(getApiErrorMessage(e));
        });
    } catch (error) {
      const validationErrors = {};
      error?.inner?.forEach((err) => {
        validationErrors[err.path] = err.message;
      });

      setErrors(validationErrors);
      setIsLoading(false);
    }
  };

  const showBalanceStrip = userData?.empType !== "IVYCLINIC";
  const balanceCards = leaveSegmentConfig.balanceCards;

  const tableRows = useMemo(
    () => tableData.map((row) => ({ ...row })),
    [tableData],
  );

  return (
    <PageShell className={styles.listRoot}>

      {showBalanceStrip && (
        <Card className={styles.balanceBoardCard}>
          <div className={styles.balanceStripInner}>
            <header className={styles.balanceBoardHeader}>
              <h2 className={`heading-sm ${styles.balanceSectionTitle}`}>
                Leave Balance
              </h2>
              <Button
                type="button"
                variant="accent"
                size="sm"
                className={styles.balanceBoardHeaderAction}
                startIcon={<Plus size={16} strokeWidth={2} aria-hidden />}
                onClick={() => setApplyOpen(true)}
              >
                Apply Leave
              </Button>
            </header>
            <div className={styles.balancePrimary}>
              {balanceCards.map((card) => {
                const raw = leaveBalance?.[card.key];
                const display = formatLeaveBalanceCard(card.key, raw);
                const tone = card.tone;
                return (
                  <div
                    key={card.key}
                    className={clsx(
                      styles.balanceTile,
                      tone === "danger" && styles.balanceTileDanger,
                    )}
                  >
                    <span
                      className={clsx(
                        styles.balanceTileIcon,
                        tone === "danger" && styles.balanceTileIconDanger,
                      )}
                    >
                      <LeaveBalanceCardIcon name={card.icon} />
                    </span>
                    <div className={styles.balanceTileBody}>
                      <span className={styles.balanceTileLabel} title={card.label}>
                        {card.label}
                      </span>
                      <div
                        className={clsx(
                          styles.balanceTileValue,
                          tone === "danger" && styles.balanceTileValueDanger,
                        )}
                      >
                        {display}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <Tooltip id="leaveTip" place="bottom" className="custom-tooltip" />
        </Card>
      )}

      {!showBalanceStrip ? (
        <div className={styles.actionsRow}>
          <Button
            type="button"
            variant="accent"
            startIcon={<Plus size={18} strokeWidth={2} aria-hidden />}
            onClick={() => setApplyOpen(true)}
          >
            Apply Leave
          </Button>
        </div>
      ) : null}

      {isLoading ? (
        <Card padding="none">
          <div className={styles.loadingBlock}>
            <ThreeDots
              height="80"
              width="80"
              radius="9"
              color="#0675d6"
              ariaLabel="three-dots-loading"
              visible
            />
          </div>
        </Card>
      ) : null}

      {!isLoading && (filteredItems?.length ?? 0) > 0 ? (

        <ResponsiveDataTable
          aria-label="Leave requests"
          columns={tableColumns}
          data={tableRows}
          defaultSortKey="from"
          defaultSortDir="desc"
          pagination
          page={currentPage}
          pageSize={limit}
          totalItems={totalLeaves}
          onPageChange={handlePageChange}
          onPageSizeChange={(n) => {
            setLimit(n);
            setCurrentPage(1);
          }}
          pageSizeOptions={[5, 10, 20, 30, 40]}
          totalLabel={`Total Requests: ${totalLeaves}`}
        />

      ) : null}

      {!isLoading && filteredItems?.length === 0 ? (
        <Card>
          <EmptyStateMessage message="No Record Available" />
        </Card>
      ) : null}

      <ApplyLeave
        open={applyOpen}
        fetchList={fetchList}
        leaveBalance={leaveBalance}
        fetchLeaveBalance={fetchLeaveBalance}
        onWFHSuccess={() => {
          setApplyOpen(false);
          if (segment !== LeaveSegment.SQE) {
            setShowWFHModal(true);
          }
        }}
        onClose={() => setApplyOpen(false)}
      />

      <Modal
        open={withdrawOpen}
        onClose={() => {
          setWithdrawOpen(false);
          handleReset();
        }}
        title="Withdraw Leave Request"
        size="lg"
        closeOnBackdropClick={false}
        closeOnEscape={false}
        footer={
          <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap w-100">
            <Button
              type="submit"
              form="withdraw-leave-form"
              variant="accent"
            >
              Submit
            </Button>
            <Button
              type="button"
              variant="light-grey"
              className="cancel-modal-withdraw"
              onClick={() => {
                setWithdrawOpen(false);
                handleReset();
              }}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <form id="withdraw-leave-form" onSubmit={handleSubmit} className="position-relative">
          {isLoading && (
            <div className={styles.overlay}>
              <div className="d-flex align-items-center justify-content-center p-4">
                <TailSpin
                  height="64"
                  width="64"
                  radius="1"
                  color="#0675d6"
                  ariaLabel="tail-spin-loading"
                  visible
                />
              </div>
            </div>
          )}
          {withdrawSummaryRows.length > 0 ? (
            <div className="mb-3">
              <ResponsiveDataTable
                aria-label="Leave request summary"
                columns={withdrawSummaryColumns}
                data={withdrawSummaryRows}
                pagination={false}
              />
            </div>
          ) : null}

          <div className="row mt-3">
            <div className="col-md-2" />
            <div className="col-md-8">
              <TextArea
                label="Reason"
                labelLayout="inline"
                id={withdrawReasonId}
                name="reason"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setErrors((prev) => ({
                    ...prev,
                    [e.target.name]: "",
                  }));
                }}
                rows={3}
                required
                error={errors.reason}
              />
            </div>
            <div className="col-md-2" />
          </div>
        </form>
      </Modal>

      <Modal
        open={showWFHModal}
        onClose={closeWFHReminderModal}
        title="Reminder"
        size="md"
        footer={
          <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap w-100">
            <Button
              type="button"
              variant="accent"
              onClick={(e) => {
                e.stopPropagation();
                closeWFHReminderModal();
              }}
            >
              OK
            </Button>
          </div>
        }
      >
        <p className="body-sm mb-0 text-center">
          It is mandatory to submit yout timesheet within 24 hours after availing WFH to avoid{" "}
          <span style={{ color: "#dc2626", fontWeight: 600 }}>
            leave without pay
          </span>
          .
        </p>
      </Modal>

      <LeaveRequestHistoryModal
        ledgerData={ledgerData}
        loading={ledgerLoading}
      />
    </PageShell>

  );
};

export default LeaveList;
